import { useContext, useMemo, useState } from 'react';
import dayjs from 'dayjs';

import { useLanguage } from '@languages/index';
import { PAYMENT_PRODUCT_TYPE } from 'constants/payment';
import { CampaignFormContext } from 'pages/campaigns/campaignForm/campaignForm.context';
import { CampaignFormStepThreeContext } from '../../campaignFormStepThree.context';
import Checkbox from 'components/checkbox';
import CampaignFormStepButtons from '../../../../../campaignFormStepButtons/campaignFormStepButtons';
import CampaignFormModificationGuide from '../../../../../campaignFormModificationGuide/campaignFormModificationGuide';
import CampaignFormFieldGroup from '../../../campaignFormFieldGroup/campaignFormFieldGroup';
import ManagerFieldGroups from '../managerFieldGroups/managerFieldGroups';
import GuidelineCheckModal from './guidelineCheckModal';
import GuidelineWebViewModal from './guidelineWebViewModal';

const BeautyCampaignFormStepThree = () => {
  const language = useLanguage();
  const languageTexts = { ...language.common, ...language.pages.campaigns };
  const { campaignFormMethods, temporaryKey, products, setShowLoader } = useContext(CampaignFormContext);
  const { isFormValid, proceedPayment, onClickNextStep } = useContext(CampaignFormStepThreeContext);

  const [termsAgreed, setTermsAgreed] = useState<boolean>(false);
  const [showGuidelineModal, setShowGuidelineModal] = useState<boolean>(false);
  const [showWebViewModal, setShowWebViewModal] = useState<boolean>(false);

  const validateNextButton = useMemo(() => {
    if (
      !isFormValid ||
      // 필수 동의가 체크되지 않은 경우
      !termsAgreed
    ) {
      return false;
    } else {
      return true;
    }
  }, [isFormValid, termsAgreed]);

  const onClickNext = () => {
    onClickNextStep({
      validateCampaign: () => {
        // @note 최소한의 방어코드들. 시간날 때 생성 조건 검증용 API를 만들어서 에러 메세지까지 모달로 띄우기
        const recruitStartsAt = campaignFormMethods.getValues('recruitStartsAt');
        if (dayjs(recruitStartsAt).isBefore(dayjs(), 'day')) {
          return false;
        }
        return true;
      },
      onSuccessStoreOrUpdateTemporaryCampaignCallback: () => {
        // 가이드라인 최종 체크 모달 띄우기
        setShowGuidelineModal(true);
      },
    });
  };

  const handleGuidelineConfirm = () => {
    setShowGuidelineModal(false);
    setShowWebViewModal(true);
  };

  const handleWebViewClose = () => {
    setShowWebViewModal(false);
  };

  const handleWebViewConfirm = () => {
    // 로더 먼저 켜기
    setShowLoader(true);
    setShowWebViewModal(false);
    // 가이드라인 확인 후 결제 진행
    proceedPayment(PAYMENT_PRODUCT_TYPE.SEEDING_PASS_BEAUTY_SINGLE);
  };

  return (
    <>
      <div className="flex flex-col gap-[65px]">
        {/* 브랜드 담당자 및 반품 관련 정보 */}
        <ManagerFieldGroups />

        <div className="flex flex-col gap-[60px]">
          {/* 필수 동의 항목 */}
          <CampaignFormFieldGroup
            wrapperClassName="!pb-0 !gap-[40px]"
            label={languageTexts.create_reels_form_beauty_terms_title}
          >
            <CampaignFormFieldGroup.FormField as="div" wrapperClassName="!gap-[4px]">
              <Checkbox
                checked={termsAgreed}
                onClick={() => setTermsAgreed(!termsAgreed)}
                onTextPress={() => setTermsAgreed(!termsAgreed)}
                text={languageTexts.create_reels_form_beauty_terms_agree}
                textClassName="font-bold"
                textStyle={{ fontSize: '15px' }}
              />
              <p className="pl-[32px] text-[14px] whitespace-pre-line">
                {languageTexts.create_reels_form_beauty_terms}
              </p>
            </CampaignFormFieldGroup.FormField>
          </CampaignFormFieldGroup>

          <CampaignFormModificationGuide />
        </div>
      </div>

      <CampaignFormStepButtons
        disabledNextButton={!validateNextButton}
        onClickNext={onClickNext}
      />

      {/* 가이드라인 최종 체크 모달 */}
      {showGuidelineModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="relative z-10">
            <GuidelineCheckModal onConfirm={handleGuidelineConfirm} />
          </div>
        </div>
      )}

      {/* 가이드라인 WebView 모달 */}
      {showWebViewModal && temporaryKey && campaignFormMethods && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={handleWebViewClose}
          />
          <div className="relative z-10">
            {(() => {
              const productID = products?.[0]?.id;

              if (!productID) {
                return null;
              }

              return (
                <GuidelineWebViewModal
                  productId={productID}
                  temporaryKey={temporaryKey}
                  onClose={handleWebViewClose}
                  onConfirm={handleWebViewConfirm}
                />
              );
            })()}
          </div>
        </div>
      )}
    </>
  );
};

export default BeautyCampaignFormStepThree;
